import React, { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Notification from '../components/Notification';
import ChangePassword from './ChangePassword';
import { UserContext } from '../contexts/UserContext';
import formatUrl from '../utils/urlFormatter';
import '../styles/settings.css';

const Settings = () => {
    const navigate = useNavigate();
    const { user, logout } = useContext(UserContext);
    const [showChangePassword, setShowChangePassword] = useState(false);
    const [showDeleteWarning, setShowDeleteWarning] = useState(false);
    const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
    const [notification, setNotification] = useState(null);

    const getUserInitials = () => {
        const f = user?.first_name?.[0] || '';
        const l = user?.last_name?.[0] || '';
        return (f + l).toUpperCase() || 'U';
    };

    const activitySettings = [
        {
            id: 'products',
            title: 'Mes Produits',
            icon: 'fa-box-open',
            action: () => navigate(`/products/user/${user?.uuid || user?.id}`)
        },
        {
            id: 'likes',
            title: 'Favoris',
            icon: 'fa-heart',
            action: () => navigate('/user/likes/')
        },
        {
            id: 'statistics',
            title: 'Statistiques',
            icon: 'fa-chart-bar',
            action: () => navigate(`/user-statistics/${user?.uuid || user?.id}`)
        },
        {
            id: 'wallet',
            title: 'Mon Portefeuille',
            icon: 'fa-wallet',
            action: () => navigate('/portefeuille')
        },
    ];

    const accountSettings = [
        {
            id: 'profile',
            title: 'Modifier le profil',
            icon: 'fa-user-edit',
            action: () => navigate('/profile')
        },
        {
            id: 'blocked-users',
            title: 'Utilisateurs bloqués',
            icon: 'fa-user-slash',
            action: () => navigate('/blocked-users')
        },
    ];

    const securitySettings = [
        {
            id: 'change-password',
            title: 'Changer le mot de passe',
            icon: 'fa-lock',
            action: () => setShowChangePassword(true)
        },
    ];

    const supportSettings = [
        {
            id: 'support',
            title: 'Nous contacter',
            icon: 'fa-headset',
            action: () => navigate('/support')
        },
    ];

    const dangerSettings = [
        {
            id: 'delete-account',
            title: 'Supprimer mon compte',
            icon: 'fa-trash-alt',
            isDanger: true,
            action: () => setShowDeleteWarning(true)
        }
    ];

    const handleCloseChangePassword = () => {
        setShowChangePassword(false);
    };

    const handlePasswordChangeSuccess = () => {
        setShowChangePassword(false);
        setNotification({
            message: 'Votre mot de passe a été modifié avec succès',
            type: 'success',
            header: 'Succès'
        });
    };

    const handleConfirmDelete = () => {
        setShowDeleteWarning(false);
        navigate('/delete-account');
    };

    const handleCancelDelete = () => {
        setShowDeleteWarning(false);
    };

    const handleLogout = async () => {
        setShowLogoutConfirm(false);
        await logout();
        navigate('/login');
    };

    return (
        <div className="settings-page">
            <div className="settings-container">
                {/* User Profile Card */}
                {user && (
                    <div className="st-profile-card" onClick={() => navigate(`/user/corner/${user.uuid || user.id}/`)} role="button" tabIndex={0}>
                        <div className="st-profile-avatar">
                            {(user.profile_picture || user.profile_picture_url) ? (
                                <img 
                                    src={formatUrl(user.profile_picture || user.profile_picture_url)} 
                                    alt="Profile" 
                                    className="st-profile-img"
                                    onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                                />
                            ) : null}
                            <span className="st-profile-initials" style={{ display: (user.profile_picture || user.profile_picture_url) ? 'none' : 'flex' }}>
                                {getUserInitials()}
                            </span>
                        </div>
                        <div className="st-profile-info">
                            <h2 className="st-profile-name">{user.first_name} {user.last_name}</h2>
                            <p className="st-profile-username">@{user.username}</p>
                        </div>
                        <i className="fas fa-chevron-right st-profile-arrow"></i>
                    </div>
                )}

                {/* Account Section */}
                <div className="st-section">
                    <p className="st-section-label">Compte</p>
                    <div className="st-section-card">
                        {accountSettings.map((option) => (
                            <div key={option.id} className="st-item" onClick={option.action} role="button" tabIndex={0}
                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); option.action(); } }}>
                                <i className={`fas ${option.icon} st-item-icon`}></i>
                                <span className="st-item-title">{option.title}</span>
                                <i className="fas fa-chevron-right st-item-arrow"></i>
                            </div>
                        ))}
                    </div>
                </div>
                {/* Activity Section */}
                <div className="st-section">
                    <p className="st-section-label">Mon activité</p>
                    <div className="st-section-card">
                        {activitySettings.map((option) => (
                            <div key={option.id} className="st-item" onClick={option.action} role="button" tabIndex={0}
                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); option.action(); } }}>
                                <i className={`fas ${option.icon} st-item-icon`}></i>
                                <span className="st-item-title">{option.title}</span>
                                <i className="fas fa-chevron-right st-item-arrow"></i>
                            </div>
                        ))}
                    </div>
                </div>
                {/* Security Section */}
                <div className="st-section">
                    <p className="st-section-label">Sécurité</p>
                    <div className="st-section-card">
                        {securitySettings.map((option) => (
                            <div key={option.id} className="st-item" onClick={option.action} role="button" tabIndex={0}
                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); option.action(); } }}>
                                <i className={`fas ${option.icon} st-item-icon`}></i>
                                <span className="st-item-title">{option.title}</span>
                                <i className="fas fa-chevron-right st-item-arrow"></i>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Support Section */}
                <div className="st-section">
                    <p className="st-section-label">Aide & support</p>
                    <div className="st-section-card">
                        {supportSettings.map((option) => (
                            <div key={option.id} className="st-item" onClick={option.action} role="button" tabIndex={0}
                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); option.action(); } }}>
                                <i className={`fas ${option.icon} st-item-icon`}></i>
                                <span className="st-item-title">{option.title}</span>
                                <i className="fas fa-chevron-right st-item-arrow"></i>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Danger Zone */}
                <div className="st-section">
                    <p className="st-section-label st-section-label-danger">Zone de danger</p>
                    <div className="st-section-card">
                        {dangerSettings.map((option) => (
                            <div key={option.id} className="st-item st-item-danger" onClick={option.action} role="button" tabIndex={0}
                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); option.action(); } }}>
                                <i className={`fas ${option.icon} st-item-icon`}></i>
                                <span className="st-item-title">{option.title}</span>
                                <i className="fas fa-chevron-right st-item-arrow"></i>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Logout */}
                <div className="st-section">
                    <div className="st-section-card">
                        <div className="st-item st-item-logout" onClick={() => setShowLogoutConfirm(true)} role="button" tabIndex={0}>
                            <i className="fas fa-sign-out-alt st-item-icon"></i>
                            <span className="st-item-title">Déconnexion</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Change Password Modal */}
            {showChangePassword && (
                <ChangePassword
                    onClose={handleCloseChangePassword}
                    onSuccess={handlePasswordChangeSuccess}
                />
            )}

            {/* Delete Account Warning Modal */}
            {showDeleteWarning && (
                <div className="modal-overlay" onClick={handleCancelDelete}>
                    <div className="modal-content delete-warning-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <i className="fas fa-exclamation-triangle" style={{ color: '#ff6b6b', fontSize: '48px' }}></i>
                        </div>
                        <h2 className="modal-title">Supprimer mon compte</h2>
                        <p className="modal-message">
                            Cette action est irréversible. Toutes vos données, produits et conversations seront définitivement supprimés.
                        </p>
                        <p className="modal-message" style={{ fontWeight: 'bold', marginTop: '10px' }}>
                            Êtes-vous sûr de vouloir continuer ?
                        </p>
                        <div className="modal-buttons">
                            <button className="modal-button cancel-button" onClick={handleCancelDelete}>
                                Annuler
                            </button>
                            <button className="modal-button confirm-button" onClick={handleConfirmDelete}>
                                Continuer
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Logout Confirmation Modal */}
            {showLogoutConfirm && (
                <div className="modal-overlay" onClick={() => setShowLogoutConfirm(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <i className="fas fa-sign-out-alt" style={{ color: '#003366', fontSize: '40px' }}></i>
                        </div>
                        <h2 className="modal-title">Se déconnecter ?</h2>
                        <p className="modal-message">
                            Vous serez redirigé vers la page de connexion et devrez vous reconnecter pour accéder à votre compte.
                        </p>
                        <div className="modal-buttons">
                            <button className="modal-button cancel-button" onClick={() => setShowLogoutConfirm(false)}>
                                Annuler
                            </button>
                            <button className="modal-button confirm-button" onClick={handleLogout}>
                                Se déconnecter
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Notification */}
            {notification && (
                <Notification
                    message={notification.message}
                    type={notification.type}
                    header={notification.header}
                    onClose={() => setNotification(null)}
                />
            )}
        </div>
    );
};

export default Settings;
